import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { appRoutes } from './app.routes';
import { AppUpdateService } from './core/services/app-update.service';
import { ActiveTimerNotificationService } from './core/services/active-timer-notification.service';
import { I18nService } from './core/i18n/i18n.service';
import { ActivitiesRepository } from './features/activities/domain/activities.repository';
import { SupabaseActivitiesRepository } from './features/activities/infrastructure/supabase-activities.repository';
import { CultureRepository } from './features/culture/domain/culture.repository';
import { SupabaseCultureRepository } from './features/culture/infrastructure/supabase-culture.repository';
import { CalendarRepository } from './features/calendar/domain/calendar.repository';
import { SupabaseCalendarRepository } from './features/calendar/infrastructure/supabase-calendar.repository';
import { DomainsRepository } from './features/domains/domain/domains.repository';
import { SupabaseDomainsRepository } from './features/domains/infrastructure/supabase-domains.repository';
import { GoalsRepository } from './features/goals/domain/goals.repository';
import { SupabaseGoalsRepository } from './features/goals/infrastructure/supabase-goals.repository';
import { ListsRepository } from './features/lists/domain/lists.repository';
import { SupabaseListsRepository } from './features/lists/infrastructure/supabase-lists.repository';
import { MealsWorkspaceRepository } from './features/meals/domain/meals-workspace.repository';
import { SupabaseMealsWorkspaceRepository } from './features/meals/infrastructure/supabase-meals-workspace.repository';
import { MealsRepository } from './features/meals/domain/meals.repository';
import { SupabaseMealsRepository } from './features/meals/infrastructure/supabase-meals.repository';
import { NotesRepository } from './features/notes/domain/notes.repository';
import { SupabaseNotesRepository } from './features/notes/infrastructure/supabase-notes.repository';
import { RecurrentTasksRepository } from './features/tasks/domain/recurrent-tasks.repository';
import { RoutinesRepository } from './features/tasks/domain/routines.repository';
import { TasksRepository } from './features/tasks/domain/tasks.repository';
import { SupabaseRecurrentTasksRepository } from './features/tasks/infrastructure/supabase-recurrent-tasks.repository';
import { SupabaseRoutinesRepository } from './features/tasks/infrastructure/supabase-routines.repository';
import { SupabaseTasksRepository } from './features/tasks/infrastructure/supabase-tasks.repository';
import { WellbeingRepository } from './features/wellbeing/domain/wellbeing.repository';
import { SupabaseWellbeingRepository } from './features/wellbeing/infrastructure/supabase-wellbeing.repository';
import { TimeTrackingRepository } from './features/time-tracking/domain/time-tracking.repository';
import { SupabaseTimeTrackingRepository } from './features/time-tracking/infrastructure/supabase-time-tracking.repository';
import { TaskCalendarSyncRepository } from './integration/task-calendar/domain/task-calendar-sync.repository';
import { SupabaseTaskCalendarSyncRepository } from './integration/task-calendar/infrastructure/supabase-task-calendar-sync.repository';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideAppInitializer(() => inject(AppUpdateService).start()),
    provideAppInitializer(() => inject(ActiveTimerNotificationService).start()),
    provideAppInitializer(() => inject(I18nService).initialize()),
    { provide: DomainsRepository, useClass: SupabaseDomainsRepository },
    { provide: ActivitiesRepository, useClass: SupabaseActivitiesRepository },
    { provide: TasksRepository, useClass: SupabaseTasksRepository },
    { provide: RecurrentTasksRepository, useClass: SupabaseRecurrentTasksRepository },
    { provide: RoutinesRepository, useClass: SupabaseRoutinesRepository },
    { provide: CalendarRepository, useClass: SupabaseCalendarRepository },
    { provide: CultureRepository, useClass: SupabaseCultureRepository },
    { provide: GoalsRepository, useClass: SupabaseGoalsRepository },
    { provide: ListsRepository, useClass: SupabaseListsRepository },
    { provide: MealsRepository, useClass: SupabaseMealsRepository },
    { provide: MealsWorkspaceRepository, useClass: SupabaseMealsWorkspaceRepository },
    { provide: NotesRepository, useClass: SupabaseNotesRepository },
    { provide: WellbeingRepository, useClass: SupabaseWellbeingRepository },
    { provide: TimeTrackingRepository, useClass: SupabaseTimeTrackingRepository },
    { provide: TaskCalendarSyncRepository, useClass: SupabaseTaskCalendarSyncRepository }
  ]
};
